import * as Sentry from "@sentry/nextjs"
import { Spinner } from "flowbite-react"
import moment from "moment"
import React, { useEffect, useMemo, useState } from "react"
import useSWR from "swr"

import CustomImage from "@components/elements/CustomImage"
import useAuth from "@hooks/useAuth"
import useToast from "@hooks/useToast"
import BookingSwLogo from "@icons/BookingSwLogo"
import { domainPlaceholder, widgetsDomainName } from "@mono/essentials/constants/options"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import * as ProductServices from "@mono/essentials/services/ProductServices"
import { SelectOptionType } from "@mono/essentials/types/GlobalConfigTypes"
import { ProductCategoryType, ProductType } from "@mono/essentials/types/OrderTypes"

import { setParentIframeHeight } from "../../../../utils/common"
import CustomerLogin from "../CustomerLogin"
import RentalProductDetailsPreview from "../RentalProductDetailsPreview"
import SelectProductModal from "../SelectproductModal"

import CategoryPreview from "./CategoryPreview"
import CategoryProductPreview from "./CategoryProductPreview"
import ProductDetailsPreview from "./ProductDetailsPreview"
import ProductPreview from "./ProductPreview"

export const Header = ({ domainDetails, heading, parentClassName = "", isIntegratedPage }) => {
  return (
    <div className={`border-b border-neutral-200 py-2 ${parentClassName}`}>
      <div className={"flex items-center justify-between mt-2 lg:mt-0 ml-2 lg;ml-0"}>
        <div className={"sm:flex items-center justify-between w-full"}>
          <div className={"flex items-center gap-2"}>
            {domainDetails?.logo ? (
              <div className={"pr-2 border-r w-fit border-neutral-300"}>
                {domainDetails?.logo !== "" ? (
                  <CustomImage
                    src={domainDetails?.logo}
                    className={"object-cover h-7 w-auto"}
                    width={80}
                    height={40}
                    fill={false}
                  />
                ) : (
                  <BookingSwLogo className={"mr-2"} />
                )}
              </div>
            ) : null}
            <div className={"flex flex-row items-center gap-2"}>
              <span
                className={
                  "lg:text18Semibold sm:text14Semibold text16Semibold lg:w-auto text-neutral-1000"
                }
              >
                {heading}
              </span>
            </div>
          </div>
          {isIntegratedPage ? (
            <div className={"mt-2 sm:mt-0"}>
              <CustomerLogin isIntegratedPage={isIntegratedPage} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export const Footer = ({ domainDetails }) => {
  return (
    <div
      className={`mt-4 flex flex-1 flex-row items-center justify-between border-t border-neutral-200 sm:px-6 sm:py-4 px-4 py-3`}
    >
      <div className={"flex items-center justify-between w-full"}>
        <div className={"flex flex-col"}>
          <div className={"paragraph13 text-neutral-1000"}>
            Powered by{" "}
            <a href={`https://www${domainPlaceholder}`} target={"_blank"} rel={"noreferrer"}>
              <span className={"text-[#2F69FE] text-Medium"}>{`${widgetsDomainName}`}</span>
            </a>
          </div>
          {domainDetails && domainDetails?.currency_iso3 ? (
            <div
              className={"text12Medium text-neutral-600"}
            >{`All prices are in ${domainDetails?.currency_iso3}`}</div>
          ) : (
            ""
          )}
        </div>
        <div className={"w-fit"}>
          <a href={`https://www${domainPlaceholder}`} target={"_blank"} rel={"noreferrer"}>
            <BookingSwLogo />
          </a>
        </div>
      </div>
    </div>
  )
}

const CustomWidgetPreview = ({ data, isIntegratedPage, domainDetails, nextStep }: any) => {
  const { showToast } = useToast()
  const orderSummaryData = data?.orderSummary ? JSON.parse(data?.orderSummary) : undefined
  const product_info_details = data?.product_info ? JSON.parse(data?.product_info) : undefined
  const [selectedProduct, setSelectedProduct] = useState<ProductType>()
  const [selectedProductCategory, setSelectedProductCategory] = useState<ProductCategoryType>()
  const [show, setShow] = useState<boolean>(false)
  const [productInfo, setProductInfo] = useState<any | undefined>(product_info_details)
  const [locationProducts, setLocationProducts] = useState([])
  const [orderSummery, setOrderSummery] = useState<any>(undefined)
  const [isFromPagePreview, setIsFromPagePreview] = useState<boolean>(false)
  const { isCustomerLoggedIn, loggedInCustomerDetails } = useAuth()

  const widgtetOnIframe = window === window.parent

  useEffect(() => {
    if (orderSummaryData && !orderSummery && Object.keys(orderSummaryData).length) {
      setOrderSummery(orderSummaryData)
      setIsFromPagePreview(true)
    }
  }, [orderSummaryData])

  const handleNextStep = (
    category_id = "",
    product_id = "",
    product_info = {},
    hasSingleProduct = false,
    summery
  ) => {
    const product_init_details = product_info ? JSON.stringify(product_info) : "{}"
    if (summery) {
      setOrderSummery(summery)
    }
    const productOrderSummery = summery ?? orderSummery
    const orderSummaryDetails = productOrderSummery
      ? {
          selectedProductDurationDetails:
            productOrderSummery?.selectedProductDurationDetails ?? product_info,
          selectedPriceTypeData: productOrderSummery?.selectedPriceTypeData,
          selectedTimeSlot: productOrderSummery?.selectedTimeSlot,
        }
      : {}

    if (window !== window.parent) {
      const params = new URLSearchParams(document.location.search)
      const category_ID = params.get("category")
      const product_ID = params.get("product_id")

      const json = {
        msg: "openFullScreenPopup",
        url: `${document.URL}&nextStep=true${category_ID ? "" : `&category=${category_id}`}${
          product_ID ? "" : `&product_id=${product_id}`
        }&product_info=${product_init_details}&orderSummary=${JSON.stringify(orderSummaryDetails)}`,
        isFrom: "smartRez",
      }

      window.parent.postMessage(JSON.stringify(json), "*")
    } else {
      if (hasSingleProduct) {
        const selectedCategory = productCategoryData?.categoryList?.find(
          (category) => category?.id === category_id
        )
        const activeProduct = selectedCategory?.activeProducts?.find(
          (product) => product?.id === product_id
        )
        setSelectedProduct(activeProduct)
      }
      setShow(true)
    }
  }

  useEffect(() => {
    if (nextStep === "true") {
      setShow(true)
    }
  }, [nextStep])

  const { data: productInfoById, isValidating: isSelectedProductDetailValidating } = useSWR(
    data.widget_type === "product_details"
      ? `selectedProductDetails-${data?.product}-${data?.product_id}`
      : null,
    async () => {
      try {
        const product_Id = data?.product_id || data?.product
        const { data: getProductInfoResponse } = await ProductServices.getProductInfoById(
          product_Id,
          {},
          isIntegratedPage
        )
        return getProductInfoResponse
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  const productDetails = useMemo(() => {
    return {
      category_id: data?.category || selectedProductCategory?.id,
      show_select_product: !!data?.category || !!selectedProductCategory?.id,
      product_description_view: data?.show_product_detail_page || "default_view",
      show_specific_product_detail: !!data?.product_id || !!data?.product || !!selectedProduct?.id,
      product_id: data?.product_id || data?.product || selectedProduct?.id,
      widget_type: data?.widget_type,
    }
  }, [selectedProductCategory, selectedProduct, data])

  const {
    data: productCategoryData = { categoryList: [], productCategory: [] },
    isLoading: isProductCategoryLoading,
    isValidating: isproductCategoryValidating,
  } = useSWR(
    `fetchProductCategory-${product_info_details}-${isCustomerLoggedIn}`,
    async () => {
      let aCategory: SelectOptionType[] = []
      try {
        let getCategoriesResponse
        if (loggedInCustomerDetails?.id) {
          getCategoriesResponse = await OrderServices.getActiveProducts(
            isIntegratedPage,
            loggedInCustomerDetails.id
          )
        } else {
          getCategoriesResponse = await OrderServices.getActiveProducts(isIntegratedPage)
        }
        aCategory = (getCategoriesResponse?.data || [])
          .filter((item) => item?.activeProducts?.length > 0)
          .map((item: ProductCategoryType) => ({
            label: item.name,
            value: item.id,
          }))
        const visibleProducts = (getCategoriesResponse?.data || []).filter(
          (item) => item?.activeProducts?.length > 0
        )
        if (widgtetOnIframe) {
          setParentIframeHeight?.()
        }

        return {
          productCategory: aCategory,
          categoryList: visibleProducts,
        }
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  const handleModalClose = (isCancel = false) => {
    if (window !== window.parent && isCancel) {
      const json = {
        msg: "hideFullScreenPopUp",
        isFrom: "smartRez",
      }
      window.parent.postMessage(JSON.stringify(json), "*")
    }
    setLocationProducts([])
    setProductInfo(undefined)
    setSelectedProduct(undefined)
    setSelectedProductCategory(undefined)
  }

  if (isproductCategoryValidating && !nextStep) {
    return (
      <div className={"flex items-center justify-center p-5"}>
        <Spinner />
      </div>
    )
  }

  const product_Info = productInfo
  if (product_Info?.start_date) {
    product_Info.start_date = moment(productInfo?.start_date)
  }
  if (product_Info?.end_date) {
    product_Info.end_date = moment(productInfo?.end_date)
  }
  return (
    <>
      {data.widget_type === "category_view" &&
        !nextStep &&
        (isproductCategoryValidating ? (
          <div className={"flex items-center justify-center p-5"}>
            <Spinner />
          </div>
        ) : (
          <>
            {widgtetOnIframe && isIntegratedPage ? (
              <div className={"overflow-auto lg:py-8"}>
                <div className={"max-w-[1180px] mx-auto"}>
                  <Header
                    domainDetails={domainDetails}
                    heading={"All Categories"}
                    isIntegratedPage={isIntegratedPage}
                  />
                  <div
                    className={`mb-8 ${isIntegratedPage ? "sm:px-8 p-2" : "max-h-[428px] overflow-auto"} lg:px-8 pt-5 px-4`}
                  >
                    <div
                      className={`grid md:grid-cols-[repeat(auto-fill,minmax(300px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-5`}
                    >
                      <CategoryPreview
                        categoryList={productCategoryData?.categoryList || []}
                        setSelectedProductCategory={setSelectedProductCategory}
                        goToNextStep={(category_id, product, product_info, hasSingleProduct) =>
                          handleNextStep(category_id, product?.id, product_info, hasSingleProduct)
                        }
                      />
                    </div>
                  </div>
                  <Footer domainDetails={domainDetails} />
                </div>
              </div>
            ) : (
              <div
                className={`mb-8 ${isIntegratedPage ? "sm:px-8 p-2" : "max-h-[428px] overflow-auto"} lg:px-8 pt-5`}
              >
                <div
                  className={`grid md:grid-cols-[repeat(auto-fill,minmax(300px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-5`}
                >
                  <CategoryPreview
                    categoryList={productCategoryData?.categoryList || []}
                    setSelectedProductCategory={setSelectedProductCategory}
                    goToNextStep={(category_id, product, product_info, hasSingleProduct) =>
                      handleNextStep(category_id, product?.id, product_info, hasSingleProduct)
                    }
                  />
                </div>
              </div>
            )}
          </>
        ))}

      {data.widget_type === "product_list" &&
        data.show_product_list_for === "all_products" &&
        !nextStep && (
          <>
            {widgtetOnIframe && isIntegratedPage ? (
              <div className={"overflow-auto lg:py-8"}>
                <div className={"max-w-[1180px] mx-auto"}>
                  <Header
                    domainDetails={domainDetails}
                    heading={"All Products"}
                    isIntegratedPage={isIntegratedPage}
                  />
                  <div className={"mt-3"}>
                    <ProductPreview
                      categoryProducts={productCategoryData?.categoryList || []}
                      showTabOrDropDown={data.display_category_as}
                      categoryListOptions={productCategoryData?.productCategory || []}
                      showDescription={data.showProductDescription}
                      isModal={isIntegratedPage}
                      goToNextStep={handleNextStep}
                      setSelectedProduct={setSelectedProduct}
                      setSelectedProductCategory={setSelectedProductCategory}
                      defaultSelectedValue={data?.display_default_category_as ?? ""}
                    />
                  </div>
                  <Footer domainDetails={domainDetails} />
                </div>
              </div>
            ) : (
              <ProductPreview
                categoryProducts={productCategoryData?.categoryList || []}
                showTabOrDropDown={data.display_category_as}
                categoryListOptions={productCategoryData?.productCategory || []}
                showDescription={data.showProductDescription}
                isModal={isIntegratedPage}
                goToNextStep={handleNextStep}
                setSelectedProduct={setSelectedProduct}
                setSelectedProductCategory={setSelectedProductCategory}
                defaultSelectedValue={data?.display_default_category_as ?? ""}
              />
            )}
          </>
        )}

      {data.widget_type === "product_list" &&
        data.show_product_list_for === "specific_category's_products" &&
        !nextStep && (
          <>
            {widgtetOnIframe && isIntegratedPage ? (
              <div className={"overflow-auto lg:py-8"}>
                <div className={"max-w-[1180px] mx-auto"}>
                  <Header
                    domainDetails={domainDetails}
                    heading={"All Products"}
                    isIntegratedPage={isIntegratedPage}
                  />
                  <div className={"flex-1 px-2 pt-5 mb-2 overflow-auto"}>
                    <div
                      className={`grid md:grid-cols-[repeat(auto-fill,minmax(300px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-5`}
                    >
                      <CategoryProductPreview
                        selectedCategory={data.category}
                        categoryList={productCategoryData?.categoryList || []}
                        goToNextStep={handleNextStep}
                        setSelectedProduct={setSelectedProduct}
                        showDescription={data.showProductDescription}
                        isModal
                      />
                    </div>
                  </div>
                  <Footer domainDetails={domainDetails} />
                </div>
              </div>
            ) : (
              <div className={"flex-1 px-2 pt-5 mb-2 overflow-auto"}>
                <div
                  className={`grid md:grid-cols-[repeat(auto-fill,minmax(300px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-5`}
                >
                  <CategoryProductPreview
                    selectedCategory={data.category}
                    categoryList={productCategoryData?.categoryList || []}
                    goToNextStep={handleNextStep}
                    setSelectedProduct={setSelectedProduct}
                    showDescription={data.showProductDescription}
                    isModal
                  />
                </div>
              </div>
            )}
          </>
        )}

      {data.widget_type === "product_details" &&
        data.product !== "" &&
        !nextStep &&
        productInfoById && (
          <>
            {widgtetOnIframe && isIntegratedPage ? (
              <div>
                <div className={"max-w-[1180px] mx-auto"}>
                  <Header
                    domainDetails={domainDetails}
                    heading={productInfoById?.name}
                    parentClassName={`sticky top-px lg:py-5 p-4 z-10 bg-white`}
                    isIntegratedPage={isIntegratedPage}
                  />
                  <div className={"pt-5"}>
                    {productInfoById?.is_dynamic_rental ? (
                      <RentalProductDetailsPreview
                        pagePreview={data.show_product_detail_page}
                        selectedProduct={selectedProduct?.id || data?.product}
                        setProductInfo={setProductInfo}
                        goToNextStep={handleNextStep}
                        isIntegratedPage={isIntegratedPage}
                        domainDetails={domainDetails}
                        formInitialValues={{
                          available_option: productInfo?.available_option ?? "",
                          duration_type: productInfo?.duration_type ?? "",
                          end_date: productInfo?.end_date ?? "",
                          end_time: productInfo?.end_time ?? "",
                          quantity: productInfo?.quantity ?? 1,
                          start_date: productInfo?.start_date,
                          start_time: productInfo?.start_time,
                          units: productInfo?.units ?? [],
                          slot_id: productInfo?.slot_id ?? "",
                          product_availability_id: productInfo?.product_availability_id ?? "",
                        }}
                        isModal={false}
                        isFullPage={widgtetOnIframe}
                        selectedProductData={productInfoById}
                        setOrderSummery={setOrderSummery}
                        setIsFromPagePreview={setIsFromPagePreview}
                      />
                    ) : (
                      <ProductDetailsPreview
                        pagePreview={data.show_product_detail_page}
                        selectedProduct={selectedProduct?.id || data?.product}
                        isIntegratedPage={isIntegratedPage}
                        domainDetails={domainDetails}
                        setProductInfo={setProductInfo}
                        goToNextStep={handleNextStep}
                        isModal={false}
                        isFullPage={widgtetOnIframe}
                        selectedProductData={productInfoById}
                        setOrderSummery={setOrderSummery}
                        setIsFromPagePreview={setIsFromPagePreview}
                      />
                    )}
                  </div>
                  <Footer domainDetails={domainDetails} />
                </div>
              </div>
            ) : (
              <div>
                {productInfoById?.is_dynamic_rental ? (
                  <RentalProductDetailsPreview
                    pagePreview={data.show_product_detail_page}
                    selectedProduct={selectedProduct?.id || data?.product}
                    setProductInfo={setProductInfo}
                    goToNextStep={handleNextStep}
                    isIntegratedPage={isIntegratedPage}
                    domainDetails={domainDetails}
                    formInitialValues={{
                      available_option: productInfo?.available_option ?? "",
                      duration_type: productInfo?.duration_type ?? "",
                      end_date: productInfo?.end_date ?? "",
                      end_time: productInfo?.end_time ?? "",
                      quantity: productInfo?.quantity ?? 1,
                      start_date: productInfo?.start_date,
                      start_time: productInfo?.start_time,
                      units: productInfo?.units ?? [],
                      slot_id: productInfo?.slot_id ?? "",
                      product_availability_id: productInfo?.product_availability_id ?? "",
                    }}
                    isModal={false}
                    selectedProductData={productInfoById}
                    setOrderSummery={setOrderSummery}
                    setIsFromPagePreview={setIsFromPagePreview}
                  />
                ) : (
                  <ProductDetailsPreview
                    pagePreview={data.show_product_detail_page}
                    selectedProduct={selectedProduct?.id || data?.product}
                    isIntegratedPage={isIntegratedPage}
                    domainDetails={domainDetails}
                    setProductInfo={setProductInfo}
                    goToNextStep={handleNextStep}
                    selectedProductData={productInfoById}
                    isModal={false}
                    setOrderSummery={setOrderSummery}
                    setIsFromPagePreview={setIsFromPagePreview}
                  />
                )}
              </div>
            )}
          </>
        )}

      {/* {data.widget_type === 'location_view' && !nextStep  &&
        <ProductLocationPreview goToNextStep={handleNextStep} isIntegratedPage={isIntegratedPage} setLocationProducts={setLocationProducts}/>
      } */}

      <SelectProductModal
        show={show}
        setShow={setShow}
        productDetails={productDetails}
        isIntegratedPage={isIntegratedPage}
        domainDetails={domainDetails}
        handleClose={handleModalClose}
        nextStep={nextStep}
        productInfo={product_Info}
        locationProducts={locationProducts}
        productCategoryData={productCategoryData?.categoryList}
        isLoading={isProductCategoryLoading || isproductCategoryValidating}
        isSelectedProductDetailValidating={isSelectedProductDetailValidating}
        isCustomWidget
        orderSummary={orderSummery}
        isFromPagePreview={isFromPagePreview}
      />
    </>
  )
}

export default CustomWidgetPreview
